import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { meetingApi, employeeApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { PageHeader, StatCard, EmptyState, Spinner, Modal } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

const TYPE_STYLES = {
  one_on_one:    { label: '1:1',         color: 'bg-blue-100 text-blue-700 border-blue-200' },
  onboarding:    { label: 'Onboarding',  color: 'bg-green-100 text-green-700 border-green-200' },
  performance:   { label: 'Performance', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  development:   { label: 'Development', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  pip_check_in:  { label: 'PIP check-in',color: 'bg-red-100 text-red-700 border-red-200' },
};

const TYPES = Object.keys(TYPE_STYLES);

function relativeTime(iso) {
  if (!iso) return '';
  const diffMs = new Date(iso).getTime() - Date.now();
  const diffMin = Math.round(diffMs / 60000);
  const abs = Math.abs(diffMin);
  const sign = diffMin >= 0 ? 'in ' : '';
  const past = diffMin < 0 ? ' ago' : '';
  if (abs < 60)       return `${sign}${abs} min${past}`;
  if (abs < 60 * 24)  return `${sign}${Math.round(abs / 60)}h${past}`;
  if (abs < 60 * 24 * 14) return `${sign}${Math.round(abs / (60 * 24))} days${past}`;
  return new Date(iso).toLocaleDateString();
}

function TypeBadge({ type }) {
  const cfg = TYPE_STYLES[type] || TYPE_STYLES.one_on_one;
  return <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border', cfg.color)}>{cfg.label}</span>;
}

function ScheduleForm({ employees, userId, onClose, onSave, saving }) {
  const [counterpart, setCounterpart] = useState('');
  const [type, setType]               = useState('one_on_one');
  const [scheduledAt, setScheduledAt] = useState('');
  const [notes, setNotes]             = useState('');
  const candidates = (employees || []).filter(e => e.id !== userId);

  const valid = counterpart && scheduledAt;

  return (
    <div className="space-y-4">
      <div>
        <label className="label">Counterpart</label>
        <select className="input" value={counterpart} onChange={e => setCounterpart(e.target.value)}>
          <option value="">Select employee…</option>
          {candidates.map(e => <option key={e.id} value={e.id}>{e.name} — {e.role?.replace('_',' ')}</option>)}
        </select>
      </div>
      <div>
        <label className="label">Type</label>
        <div className="flex flex-wrap gap-2">
          {TYPES.map(t => (
            <button key={t} type="button" onClick={() => setType(t)}
              className={clsx('px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                type === t ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}>
              {TYPE_STYLES[t].label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="label">Scheduled at</label>
        <input type="datetime-local" className="input" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} />
      </div>
      <div>
        <label className="label">Initial notes (optional)</label>
        <textarea className="input h-24 resize-none" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Topics to discuss, context, prep notes…" />
      </div>
      <div className="flex justify-end gap-3">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button disabled={!valid || saving} onClick={() => onSave({ counterpart, type, scheduledAt, notes })}
          className="btn-primary disabled:opacity-40">{saving ? 'Scheduling…' : 'Schedule meeting'}</button>
      </div>
    </div>
  );
}

function MeetingCard({ meeting, userId }) {
  const counterpart = meeting.manager_id === userId ? meeting.employee : meeting.manager;
  const role        = meeting.manager_id === userId ? 'with report' : 'with manager';
  const name        = counterpart?.name || 'Unknown';
  return (
    <div className="card p-4 flex items-center justify-between gap-4 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-sm font-semibold shrink-0">
          {name[0]}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900 truncate">{name}</p>
            <TypeBadge type={meeting.type} />
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {role} · {relativeTime(meeting.scheduled_at)} · {new Date(meeting.scheduled_at).toLocaleString()}
          </p>
        </div>
      </div>
      <Link to={`/app/meetings/${meeting.id}`} className="btn-secondary btn-sm whitespace-nowrap">
        Open <ArrowRightIcon className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

export default function Meetings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [showSchedule, setShowSchedule] = useState(false);

  const { data: meetingsRes, isLoading } = useQuery({
    queryKey: ['meetings', 'list'],
    queryFn:  () => meetingApi.list().then(r => r.data),
  });

  const { data: employees } = useQuery({
    queryKey: ['employees', 'all'],
    queryFn:  () => employeeApi.list().then(r => r.data?.data || r.data),
  });

  const createMut = useMutation({
    mutationFn: (payload) => meetingApi.create(payload),
    onSuccess:  () => { toast.success('Meeting scheduled'); qc.invalidateQueries(['meetings','list']); setShowSchedule(false); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to schedule'),
  });

  // fetch action items from first page of meetings
  const meetings = meetingsRes?.data || [];
  const actionItemQueries = useQuery({
    queryKey: ['meetings', 'action-items', meetings.map(m => m.id).join(',')],
    enabled:  meetings.length > 0,
    queryFn:  async () => {
      const detailed = await Promise.all(meetings.slice(0, 10).map(m => meetingApi.get(m.id).then(r => r.data).catch(() => null)));
      return detailed.filter(Boolean);
    },
  });

  const incompleteActionItems = useMemo(() => {
    const out = [];
    (actionItemQueries.data || []).forEach(m => {
      (m.items || []).forEach(i => {
        if (i.is_action_item && !i.completed_at) out.push({ ...i, meeting_id: m.id, meeting_type: m.type });
      });
    });
    return out;
  }, [actionItemQueries.data]);

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  const now = Date.now();
  const upcoming  = meetings.filter(m => m.status === 'scheduled' && new Date(m.scheduled_at).getTime() >= now);
  const past      = meetings.filter(m => m.status !== 'scheduled' || new Date(m.scheduled_at).getTime() < now);

  const thisMonth = new Date(); thisMonth.setDate(1); thisMonth.setHours(0,0,0,0);
  const completedThisMonth = meetings.filter(m => m.status === 'completed' && m.scheduled_at && new Date(m.scheduled_at) >= thisMonth).length;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Meetings"
        subtitle="1:1s, onboarding, performance, development, and PIP check-ins"
        actions={<button onClick={() => setShowSchedule(true)} className="btn-primary"><PlusIcon className="w-4 h-4" />Schedule 1:1</button>}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard title="Upcoming"              value={upcoming.length}             icon={CalendarDaysIcon}          color="blue" />
        <StatCard title="Completed this month"  value={completedThisMonth}          icon={CheckCircleIcon}           color="green" />
        <StatCard title="Open action items"     value={incompleteActionItems.length} icon={ClipboardDocumentListIcon} color="amber" />
      </div>

      <h2 className="section-title mb-3">Upcoming</h2>
      {upcoming.length === 0 ? (
        <EmptyState
          title="No upcoming meetings"
          description="Schedule a 1:1 to keep the feedback loop tight and cadence consistent."
          action={<button onClick={() => setShowSchedule(true)} className="btn-primary">Schedule 1:1</button>}
        />
      ) : (
        <div className="space-y-3 mb-8">
          {upcoming.map(m => <MeetingCard key={m.id} meeting={m} userId={user?.id} />)}
        </div>
      )}

      {incompleteActionItems.length > 0 && (
        <>
          <h2 className="section-title mb-3 mt-8">Open action items</h2>
          <div className="card p-2 mb-8">
            <ul className="divide-y divide-gray-100">
              {incompleteActionItems.slice(0, 12).map(item => (
                <li key={item.id} className="flex items-center justify-between p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{item.title}</p>
                    <p className="text-xs text-gray-500">
                      {item.due_date ? `Due ${new Date(item.due_date).toLocaleDateString()}` : 'No due date'} · from {TYPE_STYLES[item.meeting_type]?.label}
                    </p>
                  </div>
                  <Link to={`/app/meetings/${item.meeting_id}`} className="text-xs text-blue-600 hover:underline whitespace-nowrap ml-4">Open meeting</Link>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      <h2 className="section-title mb-3 mt-8">Past</h2>
      {past.length === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center">No past meetings yet.</p>
      ) : (
        <div className="space-y-3">
          {past.slice(0, 20).map(m => <MeetingCard key={m.id} meeting={m} userId={user?.id} />)}
        </div>
      )}

      <Modal open={showSchedule} onClose={() => setShowSchedule(false)} title="Schedule meeting" size="md">
        <ScheduleForm
          employees={employees}
          userId={user?.id}
          saving={createMut.isPending}
          onClose={() => setShowSchedule(false)}
          onSave={({ counterpart, type, scheduledAt, notes }) => {
            const managerIsMe = user?.role === 'manager' || user?.role === 'hr_admin' || user?.role === 'super_admin' || user?.role === 'director' || user?.role === 'vp';
            createMut.mutate({
              manager_id:   managerIsMe ? user.id : counterpart,
              employee_id:  managerIsMe ? counterpart : user.id,
              type,
              scheduled_at: scheduledAt,
              notes,
            });
          }}
        />
      </Modal>
    </div>
  );
}
